/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import {
  Text,
  withSitecoreContext,
  Placeholder,
} from '@sitecore-jss/sitecore-jss-react';
import get from 'lodash/get';
import defaultsDeep from 'lodash/defaultsDeep';
import compose from 'lodash/fp/compose';
import { withNamespaces } from 'react-i18next';
import { Container, Column, Row, MediaQueries } from '../../../styles/Grid';
import { keyColors } from '../../../styles/colors/index';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import createButtonCss from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import Spacer from '../../ui/Spacer';
import Picture from '../../ui/Picture';
import DotComponent from '../../ui/DotComponent';
import { NavConsumer, navbar_finite_state } from '../../../contexts/NavContext';
import { CLOUDINARY_BASE_VALUES } from '../../../utils/constants';
import { isIE } from '../../../utils/browserChecks';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { showElementCss } from '../../../utils/responsive';
import { processButtonData } from '../../../utils/analytics';
import {
  containerPaddingCss,
  containerWhiteSpaceCss,
  discoverCss,
  discoverTextCss,
  arrowCss,
  headlineCss,
  upperHeadlineCss,
  bodyCss,
  separatorCss,
  phoneWrapperCss,
  findWorkspaceCss,
  discoverRegusTextCss,
  discoverRegusTextIECss,
  imageContainerCss,
  imageCss,
  rotatorCss,
  customSpacerCss,
  searchIconCss,
  disableEventOnTextCss,
  ctasContainerCss,
} from './index.styles';

export const defaultData = {
  Images: {
    value: {
      src: '',
      alt: '',
    },
  },
  UpperHeadline: [],
  Description: { value: '' },
  DiscoverStickyText: { value: '' },
  PhoneNumber: { value: '' },
};

const imageSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 1251,
    height: 584,
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 871,
    height: 408,
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 637,
    height: 416,
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 351,
    height: 232,
  },
};

const parseUpperHeadline = values => {
  const lastItem = values[values.length - 1];
  return values.map((value, i) => {
    return value === lastItem
      ? value.fields.Label.value
      : [value.fields.Label.value, <span key={i} css={separatorCss} />];
  });
};

export class HomepageHero extends SafePureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isIE: null,
    };
  }

  componentDidMount() {
    this.setState({ isIE: isIE() });
  }

  onFindWorkspaceClicked = (componentName, openModal, e) => {
    const t = this.props.t;
    processButtonData(componentName, t('FindWorkspaceCTA'));
    openModal(e);
  };

  safeRender() {
    const { props } = this;
    const pageEditing = get(props, 'sitecoreContext.pageEditing', false);
    const t = props.t;
    const fields = defaultsDeep(props.fields, defaultData);
    const { DiscoverStickyText, UpperHeadline, Description, Images } = fields;
    const { isIE } = this.state;
    const componentName = get(props, 'rendering.componentName', '');

    return (
      <Spacer
        backgroundColor={keyColors.brown10}
        paddingCss={containerPaddingCss}
        customCss={[pageEditing ? {} : containerWhiteSpaceCss, customSpacerCss]}
      >
        <Container>
          <Row>
            <Column css={discoverCss} cols={{ sm: 1, md: 1, lg: 1 }}>
              <div css={[discoverRegusTextCss, isIE && discoverRegusTextIECss]}>
                <div css={rotatorCss}>
                  <span css={discoverTextCss}>
                    <Icon customCss={arrowCss} name="arrow-left" />
                    <Text field={DiscoverStickyText} />
                  </span>
                </div>
              </div>
            </Column>
            <Column cols={{ sm: 6, md: 7, lg: 11 }}>
              <Row>
                <Column
                  cols={{ sm: [6, 6], md: [7, 7], lg: [10, 11] }}
                  offsets={{ lg: [1, 11] }}
                >
                  <h2 css={upperHeadlineCss}>
                    {UpperHeadline && parseUpperHeadline(UpperHeadline)}
                  </h2>
                </Column>
              </Row>
              <Row>
                <Column
                  cols={{ sm: [5, 6], md: [6, 7], lg: [7, 11] }}
                  offsets={{ lg: [1, 11] }}
                >
                  <h1 css={headlineCss}>
                    {t('HomepageHeadline')}
                    <DotComponent />
                  </h1>
                </Column>
              </Row>
              <Row>
                <Column
                  cols={{ sm: [6, 6], md: [7, 7], lg: [7, 11] }}
                  offsets={{ lg: [1, 11] }}
                >
                  <p css={bodyCss}>
                    <Text field={Description} />
                  </p>
                </Column>
              </Row>
              <Row css={ctasContainerCss}>
                <Column
                  css={showElementCss(MediaQueries.lgAndUp)}
                  cols={{ lg: [1, 11] }}
                />
                <Column
                  cols={{
                    sm: [6, 6],
                    md: [3, 7],
                    lg: [3, 11],
                    xl: [3, 11],
                  }}
                >
                  <NavConsumer>
                    {({ openModal }) => (
                      <button
                        css={[createButtonCss(), findWorkspaceCss]}
                        onClick={e => {
                          this.onFindWorkspaceClicked(
                            componentName,
                            openModal,
                            e,
                          );
                        }}
                        data-sticky-phone-number-trigger
                        data-id={navbar_finite_state.SEARCH}
                      >
                        <i css={searchIconCss} className="icon-search" />
                        <span css={disableEventOnTextCss}>
                          {t('FindWorkspaceCTA')}
                        </span>
                      </button>
                    )}
                  </NavConsumer>
                </Column>
                <Column cols={{ sm: 6, md: [4, 7], lg: [7, 11], xl: [7, 11] }}>
                  <div css={phoneWrapperCss}>
                    <Placeholder
                      name="jss-phone"
                      rendering={props.rendering}
                      params={{ componentName }}
                    />
                  </div>
                </Column>
              </Row>
              <Row>
                <Column cols={{ sm: [6, 6], md: [7, 7], lg: [11, 11] }}>
                  <div css={imageContainerCss}>
                    <Picture
                      image={Images}
                      imageSizes={imageSizes}
                      pictureCss={imageCss}
                      instantLoad
                    />
                  </div>
                </Column>
              </Row>
            </Column>
          </Row>
        </Container>
      </Spacer>
    );
  }
}

HomepageHero.displayName = 'HomepageHero';

HomepageHero.defaultProps = { fields: defaultData, t: key => key };

HomepageHero.propTypes = {
  UpperHeadline: PropTypes.arrayOf({
    fields: PropTypes.shape({
      Label: PropTypes.shape({ value: PropTypes.string }),
    }),
  }),
  DiscoverStickyText: PropTypes.shape({ value: PropTypes.string }),
  Description: PropTypes.shape({ value: PropTypes.string }),
  PhoneNumber: PropTypes.shape({ value: PropTypes.string }),
  Images: PropTypes.shape({
    value: PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
  }),
  t: PropTypes.func,
};

export default compose(withNamespaces(), withSitecoreContext())(HomepageHero);
